import axios, { CanceledError } from 'axios';
import { getAccountSession } from '../auth/accountSession';
import { redirectToLogin } from './navigationUtils';
import { PROFILE_ACCESS_INVALIDATED_EVENT } from './constants';
import { getProfileRequestInfo } from './utils';
import { BACKEND_BASE_URL } from '../../config/appConfig';

let isRedirecting = false;

const getProfileInfoFromError = (error: any) => {
  const url =
    error?.config?.url ??
    error?.response?.config?.url ??
    error?.request?.responseURL;
  return getProfileRequestInfo(url);
};

export const axiosInstance = () => {
  const session = getAccountSession();
  const instance = axios.create({
    baseURL: BACKEND_BASE_URL,
    headers: {
      'Content-Type': 'application/json',
    },
    withCredentials: true,
  });

  const cleanups = new Map<object, () => void>();
  instance.interceptors.request.use((config) => {
    if (getAccountSession() !== session) throw new CanceledError();

    const controller = new AbortController();
    const callerSignal = config.signal;
    const sessionSignal = session.controller.signal;
    const abort = () => controller.abort();
    if (callerSignal?.aborted || sessionSignal.aborted) abort();
    callerSignal?.addEventListener?.('abort', abort);
    sessionSignal.addEventListener('abort', abort);
    config.signal = controller.signal;
    cleanups.set(config, () => {
      callerSignal?.removeEventListener?.('abort', abort);
      sessionSignal.removeEventListener('abort', abort);
      cleanups.delete(config);
    });
    return config;
  });

  instance.interceptors.response.use(
    (response) => {
      cleanups.get(response.config)?.();
      if (getAccountSession() !== session) throw new CanceledError();
      return response;
    },
    (error) => {
      cleanups.get(error.config)?.();
      if (getAccountSession() !== session) {
        return Promise.reject(new CanceledError());
      }
      const status = error.response?.status;
      if (status === 401 && !isRedirecting) {
        isRedirecting = true;
        redirectToLogin(window.location.href);
      } else if (status === 403 || status === 404) {
        const { profileId, isDetailsRequest, isProfileRequest } =
          getProfileInfoFromError(error);
        if (!isProfileRequest || !isDetailsRequest || !profileId) {
          return Promise.reject(error);
        }
        (error as { profileAccessHandled?: boolean }).profileAccessHandled =
          true;
        window.dispatchEvent(
          new CustomEvent(PROFILE_ACCESS_INVALIDATED_EVENT, {
            detail: { profileId, status },
          })
        );
      }
      return Promise.reject(error);
    }
  );

  return instance;
};
